// src/diagrams/routing/EdgeRoutingSystem.js - Advanced edge routing with A*, bundling, and penalties

/**
 * Main orchestrator for advanced edge routing
 * Combines A* pathfinding, edge bundling, and penalty optimization
 */
export class EdgeRoutingSystem {
  constructor(options = {}) {
    this.pathfinder = new AStarPathfinder({
      gridSize: options.gridSize || 10,
      nodePadding: options.nodePadding || 35,
      diagonalMovement: options.diagonalMovement || false,
      // Keep the path strictly orthogonal (only collinear points removed) instead of
      // collapsing a staircase toward the diagonal. Required by the Lucid export, whose
      // 'elbow' lines need the real 90° corners to route around shapes.
      preserveOrthogonal: options.preserveOrthogonal || false
    });
    
    this.bundler = new EdgeBundler({
      enabled: options.enableBundling ?? true,
      bundleThreshold: options.bundleThreshold || 0.7,
      maxBundleSize: options.maxBundleSize || 5
    });
    
    this.penaltySystem = new PenaltySystem({
      crossingPenalty: options.crossingPenalty || 15,
      lengthPenalty: options.lengthPenalty || 1,
      turnPenalty: options.turnPenalty || 3,
      proximityPenalty: options.proximityPenalty || 8,
      optimizationIterations: options.optimizationIterations ?? 3
    });
    
    this.cache = new Map();
    this.spatialIndex = new SpatialIndex();
    this.routingPolicy = options.routingPolicy || 'SMART'; // ALWAYS, AS_NEEDED, SMART

    // Serializable snapshot of the resolved config. Attached to each edge's data in
    // routeEdges() so the render-time edge components (AdvancedEdgeComponents) can route
    // with the *same* parameters the user selected, instead of a hardcoded default.
    this.config = {
      gridSize: this.pathfinder.options.gridSize,
      nodePadding: this.pathfinder.options.nodePadding,
      diagonalMovement: this.pathfinder.options.diagonalMovement,
      enableBundling: this.bundler.options.enabled,
      bundleThreshold: this.bundler.options.bundleThreshold,
      maxBundleSize: this.bundler.options.maxBundleSize,
      crossingPenalty: this.penaltySystem.options.crossingPenalty,
      lengthPenalty: this.penaltySystem.options.lengthPenalty,
      turnPenalty: this.penaltySystem.options.turnPenalty,
      proximityPenalty: this.penaltySystem.options.proximityPenalty,
      optimizationIterations: this.penaltySystem.options.optimizationIterations,
      routingPolicy: this.routingPolicy
    };
  }

  /**
   * Route all edges in a React Flow graph
   */
  routeEdges(nodes, edges, layoutDirection = 'TB') {
    // Clear cache when nodes change significantly
    this.invalidateCache(nodes);
    
    // Update spatial index for fast obstacle detection
    this.spatialIndex.update(nodes);
    
    // Convert React Flow data to internal format
    const obstacles = this.convertNodesToObstacles(nodes);
    
    // For advanced routing, we'll compute paths in the edge components
    // where we have access to the actual sourceX, sourceY, targetX, targetY
    return edges.map(edge => ({
      ...edge,
      type: this.getAdvancedEdgeType(edge.type),
      // Keep the original handles - don't override them
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      data: {
        ...edge.data,
        obstacles,
        layoutDirection,
        routingEnabled: true,
        nodes: nodes, // Pass nodes for fallback routing
        routingConfig: this.config // Selected routing params for render-time pathfinding
      }
    }));
  }

  getAdvancedEdgeType(originalType) {
    const typeMapping = {
      'defaultEdge': 'advancedEdge',
      'routedEdge': 'advancedEdge',
      'errorEdge': 'advancedErrorEdge',
      'routedErrorEdge': 'advancedErrorEdge',
      'conditionEdge': 'advancedConditionEdge',
      'routedConditionEdge': 'advancedConditionEdge'
    };
    
    return typeMapping[originalType] || 'advancedEdge';
  }

  routeEdgesWithOptimization(requests, obstacles, bundles) {
    const results = new Map();
    
    // Route bundled edges together
    for (const bundle of bundles) {
      const bundlePaths = this.routeBundle(bundle, obstacles);
      bundle.edges.forEach((edge, index) => {
        results.set(edge.id, bundlePaths[index]);
      });
    }
    
    // Route remaining individual edges
    const unbundledRequests = requests.filter(req => 
      !bundles.some(bundle => bundle.edges.some(e => e.id === req.id))
    );
    
    for (const request of unbundledRequests) {
      const path = this.routeSingleEdge(request, obstacles, results);
      results.set(request.id, path);
    }
    
    return results;
  }

  routeBundle(bundle, obstacles) {
    // For now, route each edge in bundle individually
    // Could be enhanced with true bundle routing
    return bundle.edges.map(edge => 
      this.routeSingleEdge(edge, obstacles, new Map())
    );
  }

  routeSingleEdge(request, obstacles, existingPaths = new Map()) {
    // Check cache first
    const cacheKey = this.generateCacheKey(request, obstacles);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    
    // Find base path using A*
    const basePath = this.pathfinder.findPath(
      request.source,
      request.target,
      obstacles
    );
    
    if (!basePath || basePath.length === 0) {
      return this.createFallbackPath(request.source, request.target);
    }
    
    // Apply penalty optimization
    const optimizedPath = this.penaltySystem.optimizePath(
      basePath,
      obstacles,
      Array.from(existingPaths.values())
    );
    
    // Cache result
    this.cache.set(cacheKey, optimizedPath);
    
    return optimizedPath;
  }

  convertNodesToObstacles(nodes) {
    const obstacles = nodes.map(node => {
      const nodeWidth = node.width || 200;
      const nodeHeight = node.height || 100;
      const padding = this.pathfinder.options.nodePadding || 35;
      
      // Add extra padding for decision nodes (diamond shape)
      const extraPadding = node.type === 'decisionNode' ? 40 : 0; // Increased padding
      const totalPadding = padding + extraPadding;
      
      // For entry, start, and end nodes (circular), calculate bounding box
      if (node.type === 'entryNode' || node.type === 'startNode' || node.type === 'endNode') {
        const radius = Math.max(nodeWidth, nodeHeight) / 2;
        return {
          id: node.id,
          x: node.position.x + (nodeWidth / 2) - radius - totalPadding,
          y: node.position.y + (nodeHeight / 2) - radius - totalPadding,
          width: radius * 2 + (totalPadding * 2),
          height: radius * 2 + (totalPadding * 2)
        };
      }
      
      return {
        id: node.id,
        x: node.position.x - totalPadding,
        y: node.position.y - totalPadding,
        width: nodeWidth + (totalPadding * 2),
        height: nodeHeight + (totalPadding * 2)
      };
    });

    console.log(`Converted ${nodes.length} nodes to ${obstacles.length} obstacles:`, obstacles);
    return obstacles;
  }

  convertEdgesToRequests(edges, nodes) {
    return edges.map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (!sourceNode || !targetNode) return null;
      
      // Don't calculate our own connection points - let React Flow handle this
      // We'll get the actual connection points in the edge components
      return {
        id: edge.id,
        source: null, // Will be set by edge component
        target: null, // Will be set by edge component
        sourceNode: sourceNode,
        targetNode: targetNode,
        type: edge.type || 'default',
        data: edge.data || {}
      };
    }).filter(Boolean);
  }

  getNodeConnectionPoint(node, type, handle) {
    const centerX = node.position.x + (node.width || 200) / 2;
    const centerY = node.position.y + (node.height || 100) / 2;
    
    // For simplicity, use center points - can be enhanced for specific handles
    return { x: centerX, y: centerY };
  }

  /**
   * Convert routing results back to React Flow edge format
   */
  convertPathsToReactFlowEdges(originalEdges, routedPaths, layoutDirection) {
  return originalEdges.map(edge => {
    const routedPath = routedPaths.get(edge.id);
    
    if (!routedPath || routedPath.length < 2) {
      return {
        ...edge,
        data: {
          ...edge.data,
          layoutDirection,
          routingFailed: true,
          nodes: [] // Add empty nodes array for fallback routing
        }
      };
    }

    // Determine edge type based on original type and routing result
    let edgeType = edge.type || 'advancedEdge';
    
    // Map existing types to advanced types
    const typeMapping = {
      'defaultEdge': 'advancedEdge',
      'routedEdge': 'advancedEdge',
      'errorEdge': 'advancedErrorEdge',
      'routedErrorEdge': 'advancedErrorEdge',
      'conditionEdge': 'advancedConditionEdge',
      'routedConditionEdge': 'advancedConditionEdge'
    };

    if (typeMapping[edgeType]) {
      edgeType = typeMapping[edgeType];
    }

    // Check if edge is part of a bundle
    const bundleInfo = this.getBundleInfo(edge.data);
    if (bundleInfo.isBundled) {
      edgeType = 'bundledEdge';
    }

    return {
      ...edge,
      type: edgeType,
      // Remove specific handle IDs for advanced routing since advanced edges calculate their own paths
      sourceHandle: null,
      targetHandle: null,
      data: {
        ...edge.data,
        routedPath,
        layoutDirection,
        routingSuccess: true,
        ...bundleInfo,
        // Keep original handle info for debugging/reference
        originalSourceHandle: edge.sourceHandle,
        originalTargetHandle: edge.targetHandle
      }
    };
  });
}

  getBundleInfo(edgeData) {
    if (edgeData?.bundleId) {
      return {
        isBundled: true,
        bundleId: edgeData.bundleId,
        bundleSize: edgeData.bundleSize || 1,
        bundleIndex: edgeData.bundleIndex || 0,
        bundleOffset: this.calculateBundleOffset(edgeData.bundleIndex, edgeData.bundleSize)
      };
    }
    
    return { isBundled: false };
  }

  calculateBundleOffset(index, size) {
    if (size <= 1) return 0;
    
    const spacing = 8; // pixels between bundled edges
    const center = (size - 1) / 2;
    return (index - center) * spacing;
  }

  generateCacheKey(request, obstacles) {
    const obstacleHash = obstacles
      .map(obs => `${obs.x},${obs.y},${obs.width},${obs.height}`)
      .join('|');
    return `${request.source.x},${request.source.y}-${request.target.x},${request.target.y}-${obstacleHash}`;
  }

  createFallbackPath(source, target) {
    return [source, target]; // Direct line as fallback
  }

  invalidateCache(nodes) {
    // Simple cache invalidation - could be more sophisticated
    if (this.lastNodeCount !== nodes.length) {
      this.cache.clear();
      this.lastNodeCount = nodes.length;
    }
  }
}

/**
 * A* Pathfinding implementation for obstacle avoidance
 */
class AStarPathfinder {
  constructor(options = {}) {
    this.options = {
      gridSize: 15,
      nodePadding: 25,
      diagonalMovement: false,
      ...options
    };
  }

  findPath(start, end, obstacles) {
    // Quick cache check
    const cacheKey = `path-${start.x}-${start.y}-${end.x}-${end.y}-${obstacles.length}`;
    if (this.pathCache && this.pathCache.has(cacheKey)) {
      return this.pathCache.get(cacheKey);
    }
    
    // For very large obstacle sets, limit to nearby obstacles
    const relevantObstacles = obstacles.length > 20
      ? this.filterNearbyObstacles(start, end, obstacles)
      : obstacles;

    // Adapt the grid resolution so the cell count stays bounded for very large spans (long
    // edges across a tall diagram). With a fixed fine grid those edges produce tens of
    // thousands of cells — more than A* can explore within any reasonable iteration budget —
    // so it fails and the caller falls back to a straight line through intermediate shapes.
    // Coarsening the grid (cells ≤ CELL_BUDGET) keeps A* solvable; resolution is still well
    // under the routing margin so paths remain accurate.
    const CELL_BUDGET = 14000;
    const origGridSize = this.options.gridSize;
    const rawBounds = this.calculateBounds([start, end, ...relevantObstacles]);
    const rawCells = rawBounds.gridWidth * rawBounds.gridHeight;
    if (rawCells > CELL_BUDGET) {
      this.options.gridSize = Math.ceil(origGridSize * Math.sqrt(rawCells / CELL_BUDGET));
    }

    // Create pathfinding grid (at the possibly-coarsened resolution)
    const bounds = this.calculateBounds([start, end, ...relevantObstacles]);
    const grid = this.createGrid(bounds, relevantObstacles);

    // Convert points to grid coordinates
    const startGrid = this.worldToGrid(start, bounds);
    const endGrid = this.worldToGrid(end, bounds);

    // Iteration budget covers the (bounded) cell count so A* can fully explore if needed.
    const maxIterations = Math.min(30000, Math.max(4000, bounds.gridWidth * bounds.gridHeight));
    const gridPath = this.aStarSearch(grid, startGrid, endGrid, bounds, maxIterations);

    if (!gridPath || gridPath.length === 0) {
      this.options.gridSize = origGridSize; // restore before bailing
      return null;
    }

    // Convert back to world coordinates (still at the coarsened resolution), then restore.
    const worldPath = gridPath.map(point => this.gridToWorld(point, bounds));
    this.options.gridSize = origGridSize;
    
    // Simplify path by removing unnecessary waypoints
    const simplifiedPath = this.simplifyPath(worldPath);
    
    // Cache the result
    if (!this.pathCache) {
      this.pathCache = new Map();
    }
    this.pathCache.set(cacheKey, simplifiedPath);
    
    // Cleanup cache if too large
    if (this.pathCache.size > 100) {
      const keysToDelete = Array.from(this.pathCache.keys()).slice(0, 50);
      keysToDelete.forEach(key => this.pathCache.delete(key));
    }
    
    return simplifiedPath;
  }

  // Add this new method after findPath:
  filterNearbyObstacles(start, end, obstacles) {
    const padding = 100;
    const minX = Math.min(start.x, end.x) - padding;
    const maxX = Math.max(start.x, end.x) + padding;
    const minY = Math.min(start.y, end.y) - padding;
    const maxY = Math.max(start.y, end.y) + padding;
    
    return obstacles.filter(obs => {
      const obsRight = obs.x + obs.width;
      const obsBottom = obs.y + obs.height;
      
      return !(obsRight < minX || obs.x > maxX || obsBottom < minY || obs.y > maxY);
    });
  }

  aStarSearch(grid, start, end, bounds, maxIterations = 1000) {
    const openSet = [{
      x: start.x,
      y: start.y,
      gScore: 0,
      fScore: this.heuristic(start, end),
      parent: null
    }];
    
    const closedSet = new Set();
    const openSetMap = new Map();
    openSetMap.set(`${start.x},${start.y}`, openSet[0]);
    
    let iterations = 0;
    
    while (openSet.length > 0 && iterations < maxIterations) {
      iterations++;
      // Get node with lowest fScore
      openSet.sort((a, b) => a.fScore - b.fScore);
      const current = openSet.shift();
      const currentKey = `${current.x},${current.y}`;
      
      openSetMap.delete(currentKey);
      closedSet.add(currentKey);
      
      // Check if we reached the goal
      if (current.x === end.x && current.y === end.y) {
        return this.reconstructPath(current);
      }
      
      // Check all neighbors
      const neighbors = this.getNeighbors(current, grid, bounds);
      
      for (const neighbor of neighbors) {
        const neighborKey = `${neighbor.x},${neighbor.y}`;
        
        if (closedSet.has(neighborKey)) continue;
        
        const tentativeGScore = current.gScore + this.getMovementCost(current, neighbor);
        const existingNode = openSetMap.get(neighborKey);
        
        if (!existingNode || tentativeGScore < existingNode.gScore) {
          const neighborNode = {
            x: neighbor.x,
            y: neighbor.y,
            gScore: tentativeGScore,
            fScore: tentativeGScore + this.heuristic(neighbor, end),
            parent: current
          };
          
          if (existingNode) {
            Object.assign(existingNode, neighborNode);
          } else {
            openSet.push(neighborNode);
            openSetMap.set(neighborKey, neighborNode);
          }
        }
      }
    }
    
    return null; // No path found
  }

  getNeighbors(point, grid, bounds) {
    const neighbors = [];
    const directions = this.options.diagonalMovement ? 
      [[-1,-1], [-1,0], [-1,1], [0,-1], [0,1], [1,-1], [1,0], [1,1]] :
      [[-1,0], [1,0], [0,-1], [0,1]]; // Only orthogonal
    
    for (const [dx, dy] of directions) {
      const newX = point.x + dx;
      const newY = point.y + dy;
      
      if (newX >= 0 && newX < bounds.gridWidth && 
          newY >= 0 && newY < bounds.gridHeight) {
        
        const gridKey = `${newX},${newY}`;
        if (!grid.blocked.has(gridKey)) {
          neighbors.push({ x: newX, y: newY });
        }
      }
    }
    
    return neighbors;
  }

  createGrid(bounds, obstacles) {
    const blocked = new Set();
    
    for (const obstacle of obstacles) {
      const minGridX = Math.floor((obstacle.x - bounds.minX) / this.options.gridSize);
      const maxGridX = Math.ceil((obstacle.x + obstacle.width - bounds.minX) / this.options.gridSize);
      const minGridY = Math.floor((obstacle.y - bounds.minY) / this.options.gridSize);
      const maxGridY = Math.ceil((obstacle.y + obstacle.height - bounds.minY) / this.options.gridSize);
      
      for (let x = minGridX; x <= maxGridX; x++) {
        for (let y = minGridY; y <= maxGridY; y++) {
          if (x >= 0 && x < bounds.gridWidth && y >= 0 && y < bounds.gridHeight) {
            blocked.add(`${x},${y}`);
          }
        }
      }
    }
    
    return { blocked };
  }

  calculateBounds(elements) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    for (const element of elements) {
      if (element.width !== undefined) {
        // Obstacle
        minX = Math.min(minX, element.x);
        minY = Math.min(minY, element.y);
        maxX = Math.max(maxX, element.x + element.width);
        maxY = Math.max(maxY, element.y + element.height);
      } else {
        // Point
        minX = Math.min(minX, element.x);
        minY = Math.min(minY, element.y);
        maxX = Math.max(maxX, element.x);
        maxY = Math.max(maxY, element.y);
      }
    }
    
    // Add padding
    const padding = this.options.gridSize * 5;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;
    
    return {
      minX, minY, maxX, maxY,
      width: maxX - minX,
      height: maxY - minY,
      gridWidth: Math.ceil((maxX - minX) / this.options.gridSize),
      gridHeight: Math.ceil((maxY - minY) / this.options.gridSize)
    };
  }

  worldToGrid(point, bounds) {
    return {
      x: Math.floor((point.x - bounds.minX) / this.options.gridSize),
      y: Math.floor((point.y - bounds.minY) / this.options.gridSize)
    };
  }

  gridToWorld(point, bounds) {
    return {
      x: bounds.minX + (point.x * this.options.gridSize) + (this.options.gridSize / 2),
      y: bounds.minY + (point.y * this.options.gridSize) + (this.options.gridSize / 2)
    };
  }

  heuristic(a, b) {
    if (this.options.diagonalMovement) {
      // Euclidean distance for diagonal movement
      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      return Math.sqrt(dx * dx + dy * dy);
    } else {
      // Manhattan distance for orthogonal movement
      return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    }
  }

  getMovementCost(from, to) {
    const dx = Math.abs(to.x - from.x);
    const dy = Math.abs(to.y - from.y);
    
    if (dx === 1 && dy === 1) {
      return Math.SQRT2; // Diagonal movement
    }
    return 1; // Orthogonal movement
  }

  reconstructPath(node) {
    const path = [];
    let current = node;
    
    while (current) {
      path.unshift({ x: current.x, y: current.y });
      current = current.parent;
    }
    
    return path;
  }

  simplifyPath(path) {
    if (path.length <= 2) return path;

    // Orthogonal mode: keep every real 90° corner; only drop points that are collinear with
    // their neighbours. The default distance-based simplify below would pull a staircase
    // toward the diagonal and erase the corners an elbow line needs to route around shapes.
    if (this.options.preserveOrthogonal) {
      return this.removeCollinearPoints(path);
    }

    const simplified = [path[0]];
    const tolerance = 15; // Reduced tolerance for smoother paths
    
    for (let i = 1; i < path.length - 1; i++) {
      const prev = path[i - 1];
      const current = path[i];
      const next = path[i + 1];
      
      // Calculate the distance from current point to the line between prev and next
      const distance = this.pointToLineDistance(current, prev, next);
      
      // Keep point if it's significant for the path shape
      if (distance > tolerance) {
        simplified.push(current);
      }
    }
    
    simplified.push(path[path.length - 1]);
    
    // Additional smoothing pass - remove redundant direction changes
    return this.removeRedundantPoints(simplified);
  }

  // Keep only true corners of an orthogonal path: drop a point when it lies on a straight
  // run (shares x with both neighbours, or shares y with both). Preserves obstacle-avoiding
  // 90° bends so the elbow line routes exactly as A* intended.
  removeCollinearPoints(path) {
    if (path.length <= 2) return path;
    const EPS = 0.5;
    const result = [path[0]];
    for (let i = 1; i < path.length - 1; i++) {
      const prev = result[result.length - 1];
      const cur = path[i];
      const next = path[i + 1];
      const collinear =
        (Math.abs(prev.x - cur.x) < EPS && Math.abs(cur.x - next.x) < EPS) ||
        (Math.abs(prev.y - cur.y) < EPS && Math.abs(cur.y - next.y) < EPS);
      if (!collinear) result.push(cur);
    }
    result.push(path[path.length - 1]);
    return result;
  }

  removeRedundantPoints(path) {
    if (path.length <= 3) return path;
    
    const result = [path[0]];
    
    for (let i = 1; i < path.length - 1; i++) {
      const prev = result[result.length - 1];
      const current = path[i];
      const next = path[i + 1];
      
      // Calculate angles
      const angle1 = Math.atan2(current.y - prev.y, current.x - prev.x);
      const angle2 = Math.atan2(next.y - current.y, next.x - current.x);
      const angleDiff = Math.abs(angle1 - angle2);
      
      // Keep point if it represents a significant direction change
      if (angleDiff > Math.PI / 8) { // 22.5 degrees
        result.push(current);
      }
    }
    
    result.push(path[path.length - 1]);
    return result;
  }

  pointToLineDistance(point, lineStart, lineEnd) {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) {
      return Math.sqrt(A * A + B * B);
    }
    
    const param = dot / lenSq;
    
    let xx, yy;
    
    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }
    
    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  checkPathIntersectsObstacles(start, end, obstacles) {
    return obstacles.some(obstacle => 
      this.lineIntersectsRectangle(start, end, obstacle)
    );
  }

  lineIntersectsRectangle(start, end, rect) {
    // Check if line segment intersects with rectangle
    const { x, y, width, height } = rect;
    
    // Check if either endpoint is inside the rectangle
    if (this.pointInRectangle(start, rect) || this.pointInRectangle(end, rect)) {
      return true;
    }
    
    // Check intersection with all four edges of the rectangle
    const corners = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height }
    ];
    
    for (let i = 0; i < 4; i++) {
      const cornerStart = corners[i];
      const cornerEnd = corners[(i + 1) % 4];
      
      if (this.lineSegmentsIntersect(start, end, cornerStart, cornerEnd)) {
        return true;
      }
    }
    
    return false;
  }

  pointInRectangle(point, rect) {
    return point.x >= rect.x && 
           point.x <= rect.x + rect.width &&
           point.y >= rect.y && 
           point.y <= rect.y + rect.height;
  }

  lineSegmentsIntersect(p1, p2, p3, p4) {
    const denominator = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    
    if (Math.abs(denominator) < 0.0001) return false; // Parallel lines
    
    const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denominator;
    const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denominator;
    
    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
  }
}

/**
 * Edge bundling system for reducing visual clutter
 */
class EdgeBundler {
  constructor(options = {}) {
    this.options = {
      enabled: true,
      bundleThreshold: 0.7, // Compatibility threshold (0-1)
      maxBundleSize: 5,
      subdivisions: 4,
      ...options
    };
  }

  analyzeEdgesForBundling(requests) {
    if (!this.options.enabled || requests.length < 2) {
      return [];
    }
    
    const bundles = [];
    const processed = new Set();
    
    for (let i = 0; i < requests.length; i++) {
      if (processed.has(i)) continue;
      
      const currentBundle = [requests[i]];
      processed.add(i);
      
      for (let j = i + 1; j < requests.length; j++) {
        if (processed.has(j) || currentBundle.length >= this.options.maxBundleSize) continue;
        
        const compatibility = this.calculateCompatibility(requests[i], requests[j]);
        
        if (compatibility >= this.options.bundleThreshold) {
          currentBundle.push(requests[j]);
          processed.add(j);
        }
      }
      
      if (currentBundle.length > 1) {
        bundles.push({
          edges: currentBundle,
          compatibility: this.calculateBundleCompatibility(currentBundle)
        });
      }
    }
    
    return bundles;
  }

  calculateCompatibility(edge1, edge2) {
    // Geometric compatibility based on angle, scale, position, and visibility
    const angleCompatibility = this.angleCompatibility(edge1, edge2);
    const scaleCompatibility = this.scaleCompatibility(edge1, edge2);
    const positionCompatibility = this.positionCompatibility(edge1, edge2);
    const visibilityCompatibility = this.visibilityCompatibility(edge1, edge2);
    
    return angleCompatibility * scaleCompatibility * positionCompatibility * visibilityCompatibility;
  }

  angleCompatibility(edge1, edge2) {
    const v1 = this.vectorFromEdge(edge1);
    const v2 = this.vectorFromEdge(edge2);
    
    const dotProduct = (v1.x * v2.x + v1.y * v2.y);
    const magnitude1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const magnitude2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    
    return Math.abs(dotProduct / (magnitude1 * magnitude2));
  }

  scaleCompatibility(edge1, edge2) {
    const length1 = this.edgeLength(edge1);
    const length2 = this.edgeLength(edge2);
    
    const avgLength = (length1 + length2) / 2;
    return 2 / (avgLength / Math.min(length1, length2) + Math.max(length1, length2) / avgLength);
  }

  positionCompatibility(edge1, edge2) {
    const avgLength = (this.edgeLength(edge1) + this.edgeLength(edge2)) / 2;
    const midpoint1 = this.edgeMidpoint(edge1);
    const midpoint2 = this.edgeMidpoint(edge2);
    
    const distance = Math.sqrt(
      Math.pow(midpoint1.x - midpoint2.x, 2) + 
      Math.pow(midpoint1.y - midpoint2.y, 2)
    );
    
    return avgLength / (avgLength + distance);
  }

  visibilityCompatibility(edge1, edge2) {
    // Simplified visibility check
    return 1; // Could implement line intersection checks here
  }

  vectorFromEdge(edge) {
    return {
      x: edge.target.x - edge.source.x,
      y: edge.target.y - edge.source.y
    };
  }

  edgeLength(edge) {
    const dx = edge.target.x - edge.source.x;
    const dy = edge.target.y - edge.source.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  edgeMidpoint(edge) {
    return {
      x: (edge.source.x + edge.target.x) / 2,
      y: (edge.source.y + edge.target.y) / 2
    };
  }

  calculateBundleCompatibility(edges) {
    if (edges.length < 2) return 1;
    
    let totalCompatibility = 0;
    let count = 0;
    
    for (let i = 0; i < edges.length; i++) {
      for (let j = i + 1; j < edges.length; j++) {
        totalCompatibility += this.calculateCompatibility(edges[i], edges[j]);
        count++;
      }
    }
    
    return count > 0 ? totalCompatibility / count : 1;
  }
}

/**
 * Penalty-based optimization system for route quality
 */
class PenaltySystem {
  constructor(options = {}) {
    this.options = {
      crossingPenalty: 10,
      lengthPenalty: 1,
      turnPenalty: 2,
      proximityPenalty: 5,
      optimizationIterations: 3,
      ...options
    };
  }

  optimizePath(path, obstacles, existingPaths = []) {
    if (path.length <= 2) return path;
    
    let optimizedPath = [...path];
    
    for (let iteration = 0; iteration < this.options.optimizationIterations; iteration++) {
      optimizedPath = this.optimizeIteration(optimizedPath, obstacles, existingPaths);
    }
    
    return optimizedPath;
  }

  optimizeIteration(path, obstacles, existingPaths) {
    const optimized = [...path];
    
    // Try to optimize each intermediate point
    for (let i = 1; i < optimized.length - 1; i++) {
      const bestPosition = this.findBestPosition(
        optimized[i - 1],
        optimized[i],
        optimized[i + 1],
        obstacles,
        existingPaths
      );
      
      if (bestPosition) {
        optimized[i] = bestPosition;
      }
    }
    
    return optimized;
  }

  findBestPosition(prev, current, next, obstacles, existingPaths) {
    const searchRadius = 30;
    const searchSteps = 8;
    
    let bestPosition = current;
    let bestPenalty = this.calculatePointPenalty(current, prev, next, obstacles, existingPaths);
    
    for (let dx = -searchRadius; dx <= searchRadius; dx += searchRadius / searchSteps) {
      for (let dy = -searchRadius; dy <= searchRadius; dy += searchRadius / searchSteps) {
        const candidate = { x: current.x + dx, y: current.y + dy };
        
        // Skip if position intersects with obstacles
        if (this.intersectsObstacles(candidate, obstacles)) continue;
        
        const penalty = this.calculatePointPenalty(candidate, prev, next, obstacles, existingPaths);
        
        if (penalty < bestPenalty) {
          bestPenalty = penalty;
          bestPosition = candidate;
        }
      }
    }
    
    return bestPosition !== current ? bestPosition : null;
  }

  calculatePointPenalty(point, prev, next, obstacles, existingPaths) {
    let penalty = 0;
    
    // Length penalty
    const length1 = this.distance(prev, point);
    const length2 = this.distance(point, next);
    penalty += (length1 + length2) * this.options.lengthPenalty;
    
    // Turn penalty
    const angle = this.calculateAngle(prev, point, next);
    penalty += Math.abs(angle) * this.options.turnPenalty;
    
    // Proximity penalty to existing paths
    for (const existingPath of existingPaths) {
      const proximity = this.calculateProximityToPaths(point, existingPath);
      penalty += proximity * this.options.proximityPenalty;
    }
    
    // Crossing penalty
    const crossings = this.countCrossings([prev, point, next], existingPaths);
    penalty += crossings * this.options.crossingPenalty;
    
    return penalty;
  }

  distance(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  calculateAngle(p1, p2, p3) {
    const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
    const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };
    
    const dot = v1.x * v2.x + v1.y * v2.y;
    const cross = v1.x * v2.y - v1.y * v2.x;
    
    return Math.atan2(cross, dot);
  }

  calculateProximityToPaths(point, path) {
    if (!path || path.length < 2) return 0;
    
    let minDistance = Infinity;
    
    for (let i = 0; i < path.length - 1; i++) {
      const distance = this.pointToLineDistance(point, path[i], path[i + 1]);
      minDistance = Math.min(minDistance, distance);
    }
    
    // Return inverse proximity (closer = higher penalty)
    return minDistance < 20 ? (20 - minDistance) / 20 : 0;
  }

  pointToLineDistance(point, lineStart, lineEnd) {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) {
      return Math.sqrt(A * A + B * B);
    }
    
    const param = dot / lenSq;
    
    let xx, yy;
    
    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }
    
    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  countCrossings(path, existingPaths) {
    let crossings = 0;
    
    for (const existingPath of existingPaths) {
      for (let i = 0; i < path.length - 1; i++) {
        for (let j = 0; j < existingPath.length - 1; j++) {
          if (this.linesIntersect(path[i], path[i + 1], existingPath[j], existingPath[j + 1])) {
            crossings++;
          }
        }
      }
    }
    
    return crossings;
  }

  linesIntersect(p1, p2, p3, p4) {
    const denominator = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    
    if (denominator === 0) return false; // Parallel lines
    
    const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denominator;
    const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denominator;
    
    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
  }

  intersectsObstacles(point, obstacles) {
    for (const obstacle of obstacles) {
      if (point.x >= obstacle.x && point.x <= obstacle.x + obstacle.width &&
          point.y >= obstacle.y && point.y <= obstacle.y + obstacle.height) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Spatial indexing for fast obstacle detection
 */
class SpatialIndex {
  constructor(cellSize = 100) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }

  update(objects) {
    this.grid.clear();
    
    for (const obj of objects) {
      const cells = this.getObjectCells(obj);
      
      for (const cell of cells) {
        const key = `${cell.x},${cell.y}`;
        if (!this.grid.has(key)) {
          this.grid.set(key, []);
        }
        this.grid.get(key).push(obj);
      }
    }
  }

  query(x, y, width, height) {
    const objects = new Set();
    const cells = this.getCells(x, y, width, height);
    
    for (const cell of cells) {
      const key = `${cell.x},${cell.y}`;
      const cellObjects = this.grid.get(key) || [];
      
      for (const obj of cellObjects) {
        objects.add(obj);
      }
    }
    
    return Array.from(objects);
  }

  getObjectCells(obj) {
    return this.getCells(obj.position.x, obj.position.y, obj.width || 200, obj.height || 100);
  }

  getCells(x, y, width, height) {
    const cells = [];
    const startX = Math.floor(x / this.cellSize);
    const endX = Math.floor((x + width) / this.cellSize);
    const startY = Math.floor(y / this.cellSize);
    const endY = Math.floor((y + height) / this.cellSize);
    
    for (let cx = startX; cx <= endX; cx++) {
      for (let cy = startY; cy <= endY; cy++) {
        cells.push({ x: cx, y: cy });
      }
    }
    
    return cells;
  }
}